import { useCallback, useEffect, useRef, useState } from "react";
import type { AxiosInstance } from "axios";

import { useEffectiveTenantId } from "@hooks/useEffectiveTenantId";
import { useAppSelector } from "@redux/hooks";

import {
  attachResourceTag,
  detachResourceTag,
  getResourceTags,
  getTagsPicker,
  updateResourceTags,
  type TagPickerApiRow,
  type TagsApiMountPoint,
  type TagSummaryApiRow,
} from "settings/account/components/TagsManager/requests/request";
import { colorNameToToken } from "settings/account/components/TagsManager/colorUtils";
import type { Tag } from "settings/account/components/TagsManager/types";
import {
  TAG_RESOURCE_TYPE_BY_KEY,
  TAG_RESOURCE_TYPE_BY_PICKER_KEY,
} from "settings/account/types/tag-resource-types";

/**
 * Defensive normalizer for the `resourceType` strings handed to the Tags
 * API. The Tags backend (`controllers/Tags/shared/serialize.js`) accepts
 * a strict allowlist (`ke-port`, `ke-dci`, `ke-bbaas`, …). Some
 * historical FE keys differ from that wire form — e.g. the picker /
 * wizard `ke-datacenter-interconnect` maps to wire `ke-dci`.
 *
 * Any caller that hands us a known picker key gets it remapped to the
 * matching wire value silently. Anything else passes through untouched
 * (already canonical, or a brand-new type not yet in the registry).
 *
 * Uses {@link TAG_RESOURCE_TYPE_BY_KEY} for fast pass-through and
 * {@link TAG_RESOURCE_TYPE_BY_PICKER_KEY} for the picker→wire map. Both
 * are the source of truth — keep adding entries there, not here.
 */
function normalizeResourceTypeForApi(
  raw: string | null
): string | null {
  if (!raw) return raw;
  if (TAG_RESOURCE_TYPE_BY_KEY.has(raw)) return raw;
  const aliased = TAG_RESOURCE_TYPE_BY_PICKER_KEY.get(raw);
  return aliased ? aliased.resourceType : raw;
}

/**
 * Resource-side tag bridge for the new global Tags Manager model.
 *
 * Wraps the §3.9 / §5 endpoints from `TAGS_MANAGER_BACKEND_CONTRACT.md`:
 *   - GET    `<mount>/resources/:resourceType/:resourceId/tags`        (§5.1)
 *   - POST   `<mount>/resources/:resourceType/:resourceId/tags/:tagId` (§5.3)
 *   - DELETE `<mount>/resources/:resourceType/:resourceId/tags/:tagId` (§5.4)
 *   - GET    `<mount>/tags/picker`                                     (§3.9)
 *   - PUT    `<mount>/resources/:resourceType/:resourceId/tags`        (§5.2)
 *
 * Tag CREATION (POST §3.2) is intentionally NOT exposed here. The
 * resource-side modal (`TagManagementModal`) only attaches or detaches
 * existing tags from the global catalog; creating new tags (with color,
 * category, description) is exclusively the responsibility of
 * `/account/tags-manager`. Keeping create out of this hook prevents
 * half-formed tags (no color, no category) from polluting the global
 * catalog from a resource page.
 *
 * Resource-side rows return a minimal `TagSummaryApiRow` (id, name, slug,
 * color, category) — enough to render colored chips with category tooltips.
 * `color` (semantic name like `"red"`) is mapped to the CSS var token via
 * `colorNameToToken` at the boundary.
 *
 * The hook deliberately does NOT touch `tagsStore` (the in-memory cache used
 * by `/account/tags-manager`) so detail cards never get into a cycle with the
 * manager page hooks. The TagsManager page can refresh its catalog on its
 * next mount.
 */

/**
 * UI-shape tag used by the resource-side picker. Mirrors `Tag` from
 * `TagsManager/types.ts` minus the timestamps so we can fill it from
 * either §5.1 (`TagSummaryApiRow`) or §3.2 (`TagApiRow`) responses.
 */
export type ResourceTagSummary = Pick<
  Tag,
  "id" | "name" | "color" | "category"
>;

/**
 * Slim tag shape returned by the §3.9 picker. No `category` (the endpoint
 * intentionally omits it). Used as the candidate list in the modal.
 */
export interface ResourceTagPickerCandidate {
  id: string;
  name: string;
  color: string | null;
}

const EMPTY_TAGS: ResourceTagSummary[] = [];

const apiRowToSummary = (row: TagSummaryApiRow): ResourceTagSummary => ({
  id: String(row.id),
  name: row.name,
  color: colorNameToToken(row.color),
  category: row.category,
});

const pickerRowToCandidate = (
  row: TagPickerApiRow
): ResourceTagPickerCandidate => ({
  id: String(row.id),
  name: row.name,
  color: colorNameToToken(row.color),
});

/**
 * Defensive mapper for the rich `tags` array exposed inline on resource
 * detail GETs after KD-563-2 (`TAGS-API-CONTRACT.md` §5.5):
 *
 * ```jsonc
 * { "id": 12, "name": "production", "color": "blue",
 *   "category": { "id": 1, "name": "Environment" } }
 * ```
 *
 * Accepts an unknown blob because the resource detail responses are not
 * strongly typed today. Anything that doesn't look like the contract is
 * silently dropped — the caller falls back to a §5.1 round-trip via
 * `useResourceTags.refresh()` if the array is empty.
 */
export function apiTagsToSummaries(input: unknown): ResourceTagSummary[] {
  if (!Array.isArray(input)) return [];
  const out: ResourceTagSummary[] = [];
  for (const row of input) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const id = r.id;
    const name = r.name;
    if (
      (typeof id !== "number" && typeof id !== "string") ||
      typeof name !== "string"
    ) {
      continue;
    }
    const color =
      typeof r.color === "string" || r.color === null
        ? colorNameToToken(r.color as string | null)
        : null;
    let category: ResourceTagSummary["category"] = null;
    if (
      r.category &&
      typeof r.category === "object" &&
      typeof (r.category as Record<string, unknown>).id === "number" &&
      typeof (r.category as Record<string, unknown>).name === "string"
    ) {
      const c = r.category as { id: number; name: string };
      category = { id: c.id, name: c.name };
    }
    out.push({ id: String(id), name, color, category });
  }
  return out;
}

interface UseResourceTagsArgs {
  api: AxiosInstance;
  mount?: TagsApiMountPoint;
  /** Canonical Tags Manager service-type key (e.g. `"ke-port"`). */
  resourceType: string | null;
  /** Numeric resource id. Strings are coerced; non-numeric values disable the hook. */
  resourceId: number | string | null | undefined;
  /** When false, the hook skips network calls and stays in idle state. */
  enabled?: boolean;
  /**
   * Optional pre-hydrated tags (from the resource detail GET inline `tags`
   * field — §5.5). When provided, the hook seeds `assignedTags` from this
   * list on the first mount for a given `(resourceType, resourceId)` pair
   * and SKIPS the initial §5.1 round-trip. Subsequent mutations
   * (`attach` / `detach` / `replace`) still call `refresh()` to stay in
   * sync with the server. Use {@link apiTagsToSummaries} to map the raw
   * response to this shape.
   */
  initialAssignedTags?: ResourceTagSummary[];
  /**
   * Tenant the resource lives in. Forwarded to every §5 call as a `?tenantId=`
   * query param so Global Admins / Partners viewing a child tenant's
   * resource hit the right scope (see contract §1.1 + `getTenantId(req)`).
   * Mirrors what `fetchPort`, `fetchInternetConnectionAPI`, etc. already
   * do for resource detail GETs. Omit it for self-tenant flows.
   *
   * The §3.9 picker uses the SideNav lens (`useEffectiveTenantId`) with
   * `scope=self` instead — so Available shows partner tags in Partner
   * Account view and customer tags in Customer view, without mixing both.
   */
  tenantId?: number;
}

interface SearchPickerOptions {
  search?: string;
  /** Default 20, max 100 (server-side cap). */
  limit?: number;
  /** Default 0. */
  offset?: number;
}

export interface UseResourceTagsReturn {
  /** Current tags attached to the resource. */
  assignedTags: ResourceTagSummary[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  /**
   * Clears the hook-level `error`. The modal's error alert binds to a
   * union of three sources (hook, picker, modal-local), and unless we
   * give consumers a way to reset this one explicitly, a failed
   * `attach` / `detach` / `replace` leaves the alert anchored — the
   * `onClose` of the alert can only mutate component-local state.
   */
  clearError: () => void;
  /** Re-fetch from §5.1 (e.g. after another tab modified the resource). */
  refresh: () => Promise<void>;
  /**
   * Atomic attach (§5.3). Optimistically adds `candidate` to `assignedTags`
   * before the network call; reverts on failure. Idempotent server-side, so
   * a double-click is safe — the second call returns `200` and we just
   * normalize to the server response.
   */
  attach: (candidate: ResourceTagPickerCandidate) => Promise<void>;
  /**
   * Atomic detach (§5.4). Optimistically removes `tagId` from `assignedTags`
   * before the network call; reverts on failure. Idempotent server-side.
   */
  detach: (tagId: string) => Promise<void>;
  /**
   * Picker query against §3.9. Scoped to the SideNav tenant lens with
   * `scope=self` so Partner Account view lists partner-owned tags only and
   * Customer view lists that customer's tags only. The hook automatically
   * scopes the request to the current resource via `excludeForResourceType` +
   * `excludeForResourceId`, so already-attached tags never appear in the
   * candidate list.
   */
  searchPickerCandidates: (
    options?: SearchPickerOptions
  ) => Promise<ResourceTagPickerCandidate[]>;
  /**
   * Bulk-replace the tag set with a full §5.2 PUT. Used by the Save button
   * of buffered modals where the user composes the desired list in memory
   * and only persists on confirm. The server applies the diff atomically
   * (§4.2 / §5.2 — "all operations in a single transaction").
   */
  replace: (tagIds: string[]) => Promise<ResourceTagSummary[]>;
}

/**
 * Coerces a numeric id from possibly-string inputs. Returns `null` when
 * the value isn't a positive integer (so the hook can stay idle for
 * legacy / placeholder ids like `"port-001"` or empty strings).
 */
function parseResourceId(
  raw: number | string | null | undefined
): number | null {
  if (raw == null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

export function useResourceTags(
  args: UseResourceTagsArgs
): UseResourceTagsReturn {
  const {
    api,
    mount = "kontrol-edge",
    resourceType: rawResourceType,
    resourceId,
    enabled = true,
    initialAssignedTags,
    tenantId,
  } = args;

  // SideNav lens for §3.9 picker (Partner Account vs Customer view). §5 calls
  // keep using `tenantId` (the resource's owning tenant) from args.
  const pickerLensTenantId = useEffectiveTenantId();

  // Acting tenant = the raw SideNav selection: the partner's own id in
  // Partner Account view, the customer's id in Customer view. Forwarded to
  // every §5 call as `?asTenantId=` so the backend scopes WHICH tags this
  // caller manages/sees on the resource (partner-owned vs customer-owned),
  // independent of `tenantId` (the resource's owner). Unlike
  // `useEffectiveTenantId()` — which returns `undefined` in Partner view —
  // this is always the concrete selected tenant id.
  const selectedTenantId = useAppSelector((s) => s.selection.tenantId);
  const asTenantId =
    typeof selectedTenantId === "number" ? selectedTenantId : undefined;

  // Normalize once at the boundary so every downstream call (refresh,
  // attach, detach, replace, picker) uses the wire-canonical type. The
  // backend allowlist rejects anything else with a 400 — see
  // `controllers/Tags/shared/serialize.js` for the canonical list.
  const resourceType = normalizeResourceTypeForApi(rawResourceType);

  const numericId = parseResourceId(resourceId);
  const canCall = enabled && !!resourceType && numericId !== null;

  // Eager seeding at mount: when the parent has handed us an inline
  // snapshot (§5.5) and the resource pair is callable, we want
  // `assignedTags` populated FROM THE FIRST RENDER — not after the
  // hydration `useEffect` runs. Otherwise downstream effects (e.g. the
  // modal's `desiredIds` seed) read an empty array on the first tick and
  // their ref-guards lock them out before the real data arrives.
  //
  // `initialAssignedTags === undefined` means "no seed provided"; an
  // empty array counts as "explicit empty seed" and suppresses the
  // initial fetch.
  const seedProvidedAtMount = initialAssignedTags !== undefined;
  const initialKeyAtMount =
    canCall && resourceType && numericId !== null
      ? `${resourceType}:${numericId}`
      : null;
  const useSeedAtMount = canCall && seedProvidedAtMount;

  const [assignedTags, setAssignedTags] = useState<ResourceTagSummary[]>(
    () => (useSeedAtMount ? initialAssignedTags ?? EMPTY_TAGS : EMPTY_TAGS)
  );
  // Pre-flag loading when we know the very first effect will fire a
  // §5.1 fetch (no seed but resource is callable). Consumers can rely on
  // `loading === true` as "we're hydrating, wait" instead of having to
  // detect the empty-array-then-data transition.
  const [loading, setLoading] = useState<boolean>(
    canCall && !seedProvidedAtMount
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Tracks the last resource pair we hydrated for so a stale response
  // from a prior resource doesn't overwrite the current one.
  const lastHydrationKeyRef = useRef<string | null>(
    useSeedAtMount ? initialKeyAtMount : null
  );
  // Tracks the resource pair for which we already consumed the inline seed
  // so navigating to a different resource re-applies a fresh seed (and
  // staying on the same resource doesn't re-overwrite local optimistic
  // state with the parent's stale snapshot).
  const lastInitialKeyRef = useRef<string | null>(
    useSeedAtMount ? initialKeyAtMount : null
  );
  // When `useState()` already seeded `assignedTags` from the inline §5.5
  // snapshot, the very first run of the hydration `useEffect` would
  // otherwise fall through to a redundant `refresh()` (the seed-path `if`
  // misses because `lastInitialKeyRef` is already set to the same key).
  // That redundant fetch wins over the seed when it fails — e.g. partner
  // view, where `useEffectiveTenantId()` returns `undefined` and the
  // backend's `findOne({ id, tenantId: <jwt> })` doesn't honor the
  // partner→child tenant hierarchy → 404 → `setAssignedTags(EMPTY_TAGS)`
  // wipes the visible chips a tick after they appeared.
  //
  // The ref-guard skips that first refresh; subsequent renders fall
  // through to the normal flow.
  const skipFirstRefreshRef = useRef<boolean>(useSeedAtMount);

  const refresh = useCallback(async () => {
    if (!canCall || !resourceType || numericId === null) return;
    const key = `${resourceType}:${numericId}`;
    lastHydrationKeyRef.current = key;
    setLoading(true);
    setError(null);
    try {
      const res = await getResourceTags(
        api,
        mount,
        resourceType,
        numericId,
        tenantId,
        asTenantId
      );
      if (lastHydrationKeyRef.current !== key) return;
      const envelope = res.data;
      if (!envelope.success) {
        // Envelope-level "not found": the backend returned 200 with
        // success:false. Treat as empty (see catch block for the 404 case).
        if (envelope.message === "Resource not found") {
          setAssignedTags(EMPTY_TAGS);
          return;
        }
        throw new Error(envelope.message || "Failed to load resource tags");
      }
      setAssignedTags(envelope.data.map(apiRowToSummary));
    } catch (e) {
      if (lastHydrationKeyRef.current !== key) return;
      // "Resource not found" = the resource has no row in the backend's
      // Resources table yet (e.g. legacy entities created before Tags
      // Manager shipped). From the user's POV this is indistinguishable
      // from "no tags attached", so we treat it as an empty list and
      // suppress the error. Axios may surface this either as an HTTP 404
      // (status === 404) or as a 200 with success:false (handled above).
      const axiosLike = e as {
        response?: { status?: number; data?: { message?: string } };
      };
      const isResourceNotFound =
        axiosLike?.response?.status === 404 ||
        axiosLike?.response?.data?.message === "Resource not found";
      if (isResourceNotFound) {
        setAssignedTags(EMPTY_TAGS);
        return;
      }
      // Soft-fail for other errors: leave the list empty so callers can
      // still render the "no tags" affordance and try again. Error is
      // surfaced for UI (real failures: 500s, network, permissions).
      setAssignedTags(EMPTY_TAGS);
      setError(e instanceof Error ? e.message : "Failed to load resource tags");
    } finally {
      if (lastHydrationKeyRef.current === key) {
        setLoading(false);
      }
    }
  }, [api, mount, resourceType, numericId, canCall, tenantId, asTenantId]);

  useEffect(() => {
    if (!canCall || !resourceType || numericId === null) {
      setAssignedTags(EMPTY_TAGS);
      setLoading(false);
      setError(null);
      skipFirstRefreshRef.current = false;
      return;
    }
    const key = `${resourceType}:${numericId}`;
    // Seed path: the parent has the rich `tags` array from the resource
    // detail GET (§5.5). Use it on the first mount per resource pair and
    // skip the §5.1 round-trip — the chip strip renders instantly.
    if (initialAssignedTags && lastInitialKeyRef.current !== key) {
      lastInitialKeyRef.current = key;
      lastHydrationKeyRef.current = key;
      setAssignedTags(initialAssignedTags);
      setLoading(false);
      setError(null);
      skipFirstRefreshRef.current = false;
      return;
    }
    // First run after `useState()` already seeded the state — don't
    // redundantly fire §5.1, the parent's snapshot is the truth for now.
    // Mutations (`replace`, `attach`, `detach`) call `refresh()` themselves
    // to keep the store in sync.
    if (skipFirstRefreshRef.current) {
      skipFirstRefreshRef.current = false;
      return;
    }
    // No seed (or already consumed for this key) → fall back to §5.1.
    void refresh();
  }, [refresh, canCall, resourceType, numericId, initialAssignedTags]);

  const attach = useCallback(
    async (candidate: ResourceTagPickerCandidate): Promise<void> => {
      if (!resourceType || numericId === null) {
        throw new Error("Cannot attach tag: resource is not identified");
      }
      const tagIdNum = Number(candidate.id);
      if (!Number.isFinite(tagIdNum) || !Number.isInteger(tagIdNum)) {
        throw new Error(`Tag id "${candidate.id}" is not a valid integer`);
      }
      // Optimistic insert — keep the existing list ordering and append
      // the new candidate at the end (matches §5.1 createdAt ASC order).
      const previous = assignedTags;
      if (!previous.some((t) => t.id === candidate.id)) {
        setAssignedTags([
          ...previous,
          {
            id: candidate.id,
            name: candidate.name,
            color: candidate.color,
            category: null,
          },
        ]);
      }
      setSaving(true);
      setError(null);
      try {
        await attachResourceTag(
          api,
          mount,
          resourceType,
          numericId,
          tagIdNum,
          tenantId,
          asTenantId
        );
        // Re-hydrate the slot from §5.1 so we get the proper category and
        // the canonical createdAt ordering. Cheap and keeps the chip strip
        // truthful even if another tab concurrently changed something.
        await refresh();
      } catch (e) {
        setAssignedTags(previous);
        const msg =
          e instanceof Error ? e.message : "Failed to attach tag";
        setError(msg);
        throw new Error(msg);
      } finally {
        setSaving(false);
      }
    },
    [
      api,
      mount,
      resourceType,
      numericId,
      assignedTags,
      refresh,
      tenantId,
      asTenantId,
    ]
  );

  const detach = useCallback(
    async (tagId: string): Promise<void> => {
      if (!resourceType || numericId === null) {
        throw new Error("Cannot detach tag: resource is not identified");
      }
      const tagIdNum = Number(tagId);
      if (!Number.isFinite(tagIdNum) || !Number.isInteger(tagIdNum)) {
        throw new Error(`Tag id "${tagId}" is not a valid integer`);
      }
      const previous = assignedTags;
      setAssignedTags(previous.filter((t) => t.id !== tagId));
      setSaving(true);
      setError(null);
      try {
        await detachResourceTag(
          api,
          mount,
          resourceType,
          numericId,
          tagIdNum,
          tenantId,
          asTenantId
        );
      } catch (e) {
        setAssignedTags(previous);
        const msg =
          e instanceof Error ? e.message : "Failed to detach tag";
        setError(msg);
        throw new Error(msg);
      } finally {
        setSaving(false);
      }
    },
    [api, mount, resourceType, numericId, assignedTags, tenantId, asTenantId]
  );

  const searchPickerCandidates = useCallback(
    async (
      options: SearchPickerOptions = {}
    ): Promise<ResourceTagPickerCandidate[]> => {
      if (!resourceType || numericId === null) return [];
      const res = await getTagsPicker(api, mount, {
        search: options.search,
        excludeForResourceType: resourceType,
        excludeForResourceId: numericId,
        limit: options.limit,
        offset: options.offset,
        tenantId: pickerLensTenantId,
        scope: "self",
      });
      const envelope = res.data;
      if (!envelope.success) {
        throw new Error(
          envelope.message || "Failed to load tag picker candidates"
        );
      }
      // Server-side `excludeFor*` already filters tags currently in the
      // pivot, so we trust the server here. Keeping the callback free of
      // `assignedTags` is critical: callers that wire this into a useEffect
      // would otherwise re-fire on every assigned-tag mutation, which
      // multiplies picker requests after a Save. Local de-dup (e.g. for
      // pending detaches in a buffered modal) belongs in the consumer.
      return envelope.data.items.map(pickerRowToCandidate);
    },
    [api, mount, resourceType, numericId, pickerLensTenantId]
  );

  const replace = useCallback(
    async (tagIds: string[]): Promise<ResourceTagSummary[]> => {
      if (!resourceType || numericId === null) {
        throw new Error("Cannot save tags: resource is not identified");
      }
      const numericIds = tagIds
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && Number.isInteger(n));
      setSaving(true);
      setError(null);
      try {
        const res = await updateResourceTags(
          api,
          mount,
          resourceType,
          numericId,
          { tagIds: numericIds },
          tenantId,
          asTenantId
        );
        const envelope = res.data;
        if (!envelope.success) {
          throw new Error(envelope.message || "Failed to update resource tags");
        }
        const next = envelope.data.map(apiRowToSummary);
        setAssignedTags(next);
        return next;
      } catch (e) {
        const msg =
          e instanceof Error ? e.message : "Failed to update resource tags";
        setError(msg);
        throw new Error(msg);
      } finally {
        setSaving(false);
      }
    },
    [api, mount, resourceType, numericId, tenantId, asTenantId]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    assignedTags,
    loading,
    saving,
    error,
    clearError,
    refresh,
    attach,
    detach,
    searchPickerCandidates,
    replace,
  };
}
